import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { ValuesMap } from '@/common/types';
import { API_URL } from '@/dtos';
import { useGetCurrentOrganizationType } from '@/utils/hooks/useGetCurrentOrganizationType';

import { useDownloadReportParams } from '../hooks/useDownloadReportParams';
import { ReportTypes } from '../report.types';

const downloadReportQuery = (
  id: string,
  _organizationType: OrganizationTypes | null,
  type: ReportTypes | null,
  params?: ValuesMap<string>
) => {
  switch (type) {
    case ReportTypes.Replenishment:
      return api.get(API_URL.REPORTS_REPLENISHMENT_INVOICE_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: {
          employer_id: id,
          invoice_id: params?.['invoice-ids'],
        },
      });
    case ReportTypes.Contribution:
      return api.get(API_URL.REPORTS_CONTRIBUTION_INVOICE_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: {
          employer_id: id,
          invoice_id: params?.['invoice-ids'],
        },
      });
    case ReportTypes.Prefund:
      return api.get(API_URL.REPORTS_PREFUND_INVOICE_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: {
          employer_id: id,
          invoice_id: params?.['invoice-ids'],
        },
      });

    default:
      return null;
  }
};

export const useDownloadReportQuery = (
  id: string,
  startDownload: boolean,
  type: ReportTypes | null,
  customParams?: ValuesMap<string>,
  _invoiceId?: string
) => {
  const { currentType } = useGetCurrentOrganizationType();
  const params = useDownloadReportParams(type);
  const queryKey = useMemo(() => `${type}QueryKey`, [type]);
  const { data, isLoading, refetch, isError } = useQuery(
    [queryKey],
    () => downloadReportQuery(id, currentType, type, customParams || params),
    {
      enabled: startDownload,
    }
  );

  const fileName = useMemo(() => {
    const fileNameHeaderValue = data?.headers?.['content-disposition'];
    if (!fileNameHeaderValue) return null;

    const startIndex = fileNameHeaderValue?.indexOf('filename=');
    if (startIndex === -1) return null;

    const endIndex = fileNameHeaderValue.indexOf(';', startIndex);
    const [fileNameFromHeader] = fileNameHeaderValue
      .substring(startIndex + 9, endIndex !== -1 ? endIndex : undefined)
      .trim()
      .split('.');

    return fileNameFromHeader;
  }, [data]);

  const queryClient = useQueryClient();
  const remove = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKey],
    });
  }, [queryClient, queryKey]);
  return {
    data: data?.data,
    fileName,
    isLoading,
    refetch,
    remove,
    isError,
  };
};
